import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { EmailLotesPanel } from '@/components/email/EmailLotesPanel';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import { useAuth } from '@/contexts/AuthContext';
import { useDisparos, DisparosProvider } from '@/contexts/DisparosContext';
import { useConfirmation } from '@/hooks/useConfirmation';
import { formatDateBR } from '@/utils/dateUtils';
import {
  Search,
  BarChart,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  X,
  DollarSign,
  FileText,
  TrendingUp,
  Send,
  AlertTriangle,
  Trash2,
  HelpCircle,
  Pause,
  Download,
  Database,
  UserCheck,
  Calendar,
  RefreshCw,
  FileSearch
} from 'lucide-react';
import { apiService } from '@/lib/api';
import { ChartContainer } from '@/components/ui/chart';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import ImportBoletosModal from '@/components/ImportBoletosModal';
import ImportDistribuicaoModal from '@/components/ImportDistribuicaoModal';
import ConciliarBoletosModal from '@/components/ConciliarBoletosModal';
import DailyProgressVisualization from '@/components/DailyProgressVisualization';
import HistoricoLotesModal from '@/components/HistoricoLotesModal';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

interface DisparoLote {
  id: number;
  nome: string;
  tipo_envio: string;
  vencimento: string;
  total_boletos: number;
  total_enviados: number;
  total_fila?: number;
  total_aguardando_confirmacao?: number;
  total_pendentes: number;
  total_invalidos?: number;
  total_bloqueados?: number;
  total_falhas: number;
  total_baixados?: number;
  valor_total: number;
  status: string;
  created_at: string;
  updated_at: string;
}

interface DisparoBoleto {
  id: number;
  lote_id: number;
  nome: string;
  placa: string;
  modelo: string;
  valor: number;
  vencimento: string;
  contato: string;
  status: string;
  motivo_rejeicao?: string;
  data_envio?: string;
  hora_envio?: string;
  created_at: string;
}

const ClientDisparosContent = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { lotes, isLoading: isLoadingLotes, refreshLotes } = useDisparos();
  const { confirm, ConfirmationDialog } = useConfirmation();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLote, setSelectedLote] = useState<DisparoLote | null>(null);
  const [loteDisparos, setLoteDisparos] = useState<DisparoBoleto[]>([]);
  const [disparosLoading, setDisparosLoading] = useState(false);
  const [disparoSearchTerm, setDisparoSearchTerm] = useState('');
  const [disparoStatusFilter, setDisparoStatusFilter] = useState('all');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<'pdf' | 'xlsx'>('pdf');
  const [exportStatusFilter, setExportStatusFilter] = useState('all');
  const [hasImportIntegration, setHasImportIntegration] = useState(false);
  const [clientSystemType, setClientSystemType] = useState<string>('');
  const [loadingIntegration, setLoadingIntegration] = useState(false);
  const [showDistribuicaoModal, setShowDistribuicaoModal] = useState(false);
  const [selectedLoteForDistribuicao, setSelectedLoteForDistribuicao] = useState<number | null>(null);
  const [distribuicaoStatus, setDistribuicaoStatus] = useState<{ [key: number]: { hasDistribution: boolean; total: number } }>({});
  const [showConciliacaoModal, setShowConciliacaoModal] = useState(false);
  const [selectedLoteForConciliacao, setSelectedLoteForConciliacao] = useState<number | null>(null);
  
  // CORREÇÃO: Adicionar estado local para controlar loading do botão "Enviar Agora"
  const [isSendingLote, setIsSendingLote] = useState(false);
  const [isMarcandoFalhasComoPendente, setIsMarcandoFalhasComoPendente] = useState(false);
  const [isMarcandoBaixadosComoPendente, setIsMarcandoBaixadosComoPendente] = useState(false);
  const [enviarDialog, setEnviarDialog] = useState<{ open: boolean; loteId: number | null; emailEnabled: boolean }>({ open: false, loteId: null, emailEnabled: false });
  const [enviarEmailJunto, setEnviarEmailJunto] = useState(false);
  const [clienteEmailCanalEnabled, setClienteEmailCanalEnabled] = useState(false);
  
  // Estados para seleção de conexão (implementação simples)
  const [clientConnections, setClientConnections] = useState<any[]>([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState<number | null>(null);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [showHistoricoModal, setShowHistoricoModal] = useState(false);
  const [disparoTab, setDisparoTab] = useState<'whatsapp' | 'email'>('whatsapp');

  // Se o canal de email estiver desabilitado e a aba ativa for 'email', volta para WhatsApp.
  useEffect(() => {
    if (!clienteEmailCanalEnabled && disparoTab === 'email') {
      setDisparoTab('whatsapp');
    }
  }, [clienteEmailCanalEnabled, disparoTab]);

  // Opções de status para o filtro
  const statusOptions = [
    { value: 'all', label: 'Todos os Status' },
    { value: 'PENDENTE', label: 'Pendente' },
    { value: 'FILA', label: 'Fila' },
    { value: 'AGUARDANDO_CONFIRMACAO', label: 'Aguardando Confirmação' },
    { value: 'ENVIADO', label: 'Enviado' },
    { value: 'FALHA', label: 'Falha' },
    { value: 'CONTATO_INVALIDO', label: 'Contato Inválido' },
    { value: 'BAIXADO', label: 'Baixado' }
  ];

  // Carregar conexões do cliente
  const loadClientConnections = async () => {
    if (!user?.client_id) return;
    
    try {
      setLoadingConnections(true);
      console.log('🔍 [ClientDisparos] Carregando conexões para cliente:', user.client_id);
      
      const response = await apiService.get('/whatsapp/client/current');
      const allConnections = response?.connections || [];
      
      // Filtrar apenas conexões do tipo "oficial"
      const connections = allConnections.filter((conn: any) => 
        conn.connection_type === 'oficial'
      );
      
      console.log('📱 [ClientDisparos] Total conexões:', allConnections.length);
      console.log('📱 [ClientDisparos] Conexões oficiais:', connections.length);
      console.log('📱 [ClientDisparos] Conexões filtradas:', connections);
      
      setClientConnections(connections);
      
      // Selecionar primeira conexão automaticamente
      if (connections.length > 0 && !selectedConnectionId) {
        const firstConnection = connections[0];
        setSelectedConnectionId(firstConnection.id);
        console.log('🎯 [ClientDisparos] Selecionando primeira conexão:', firstConnection.name);
      }
      
    } catch (error) {
      console.error('❌ [ClientDisparos] Erro ao carregar conexões:', error);
    } finally {
      setLoadingConnections(false);
    }
  };

  // Verificar integração de importação quando componente montar
  useEffect(() => {
    if (user?.client_id) {
      checkImportIntegration(user.client_id);
      loadClientConnections();
    }
  }, [user?.client_id]);

  // Função para verificar integração de importação (Hinova ou South)
  const checkImportIntegration = async (clientId: number) => {
    setLoadingIntegration(true);
    try {
      console.log(`🔍 [ClientDisparos] Verificando integração para cliente ${clientId}`);
      
      // Buscar dados do cliente diretamente
      const clientResponse = await apiService.get(`/clients/${clientId}`);
      console.log(`📊 [ClientDisparos] Resposta do cliente:`, clientResponse);
      
      if (clientResponse && clientResponse.id) {
        const systemType = clientResponse.system_management;
        console.log(`🔧 [ClientDisparos] Sistema detectado: "${systemType}"`);
        setClientSystemType(systemType || '');
        setClienteEmailCanalEnabled(!!clientResponse.email_canal_enabled);
        
        // Verificar se tem integração ativa para Hinova ou South
        if (systemType === 'hinova') {
          console.log(`🏢 [ClientDisparos] Verificando integração Hinova...`);
          try {
            const response = await apiService.get(`/integrations/client/${clientId}`);
            console.log(`📋 [ClientDisparos] Resposta integração Hinova:`, response);
            
            const hasHinovaIntegration = response.integration?.system_type === 'hinova' && response.integration?.is_active;
            console.log(`✅ [ClientDisparos] Hinova integração ativa: ${hasHinovaIntegration}`);
            setHasImportIntegration(hasHinovaIntegration);
          } catch (hinovaError) {
            console.warn(`⚠️ [ClientDisparos] Erro ao verificar integração Hinova:`, hinovaError);
            setHasImportIntegration(false);
          }
        } else if (systemType === 'south' || systemType === 'south-v0') {
          console.log(`🌟 [ClientDisparos] Verificando integração South (${systemType})...`);
          const hasToken = !!clientResponse.system_token;
          const hasApiUrl = !!clientResponse.api_base_url;
          console.log(`🔑 [ClientDisparos] South - Token: ${hasToken}, API URL: ${hasApiUrl}`);
          
          const hasSouthIntegration = hasToken && hasApiUrl;
          console.log(`✅ [ClientDisparos] South integração ativa: ${hasSouthIntegration}`);
          setHasImportIntegration(hasSouthIntegration);
        } else {
          console.log(`❌ [ClientDisparos] Sistema "${systemType}" não suportado para importação`);
          setHasImportIntegration(false);
        }
      } else {
        console.warn(`⚠️ [ClientDisparos] Resposta inválida do cliente:`, clientResponse);
        setHasImportIntegration(false);
        setClientSystemType('');
      }
    } catch (error) {
      console.error('❌ [ClientDisparos] Erro ao verificar integração:', error);
      setHasImportIntegration(false);
      setClientSystemType('');
    } finally {
      setLoadingIntegration(false);
    }
  };

  const loadLoteDisparos = async (loteId: number) => {
    try {
      setDisparosLoading(true);
      console.log(`🔍 [ClientDisparos] Carregando TODOS os disparos do lote ${loteId}`);
      
      // Solicitar todos os disparos (limit=0) para carregar o lote completo
      const response = await apiService.get(`/disparos/lotes/${loteId}?limit=0`);
      setLoteDisparos(response.disparos || []);
      
      // CORREÇÃO: Atualizar selectedLote com informações completas do backend
      if (response.lote && response.estatisticas) {
        const loteInfo = response.lote;
        const estatisticas = response.estatisticas;
        console.log('📊 [ClientDisparos] Informações do lote recebidas do backend:', loteInfo);
        console.log('📊 [ClientDisparos] Estatísticas recebidas do backend:', estatisticas);
        
        const loteAtualizado = {
          ...selectedLote,
          id: loteInfo.id,
          nome: loteInfo.nome,
          tipo_envio: loteInfo.tipo_envio,
          status: loteInfo.status,
          data_vencimento: loteInfo.vencimento,
          valor_total: loteInfo.valor_total,
          total_boletos: estatisticas.total,
          total_enviados: estatisticas.enviados,
          total_aguardando_confirmacao: estatisticas.aguardando_confirmacao,
          total_pendentes: estatisticas.pendentes,
          total_fila: estatisticas.fila,
          total_falhas: estatisticas.falhas,
          total_invalidos: estatisticas.invalidos,
          total_baixados: estatisticas.baixados
        };
        
        console.log('📊 [ClientDisparos] selectedLote após atualização:', loteAtualizado);
        setSelectedLote(loteAtualizado);
      } else {
        console.log('❌ [ClientDisparos] Informações do lote não encontradas na resposta do backend');
      }
    } catch (error) {
      console.error('❌ [ClientDisparos] Erro ao carregar disparos do lote:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar disparos do lote",
        variant: "destructive"
      });
    } finally {
      setDisparosLoading(false);
    }
  };

  const handleViewDetails = (lote: DisparoLote) => {
    setSelectedLote(lote);
    setShowDetailsModal(true);
    setDisparoSearchTerm('');
    setDisparoStatusFilter('all');
    loadLoteDisparos(lote.id);
  };

  // Função para filtrar disparos para exportação
  const getFilteredDisparosForExport = () => {
    return loteDisparos.filter(disparo => {
      return exportStatusFilter === 'all' || disparo.status === exportStatusFilter;
    });
  };

  // Função para exportar em PDF
  const exportToPDF = () => {
    const filteredDisparos = getFilteredDisparosForExport();
    const isAniversario = selectedLote?.tipo_envio === 'ANIVERSARIO';
    
    const doc = new jsPDF('l', 'mm', 'a4');
    
    doc.setFontSize(16);
    doc.text(`Relatório do Lote ${selectedLote?.id}`, 14, 22);
    doc.setFontSize(10);
    doc.text(`Nome: ${selectedLote?.nome}`, 14, 30);
    doc.text(`Tipo de Envio: ${selectedLote?.tipo_envio}`, 14, 35);
    doc.text(`Status: ${selectedLote?.status}`, 14, 40);
    doc.text(`Total de Disparos: ${selectedLote?.total_boletos}`, 14, 45);
    doc.text(`Filtro Aplicado: ${exportStatusFilter === 'all' ? 'Todos os Status' : exportStatusFilter}`, 14, 50);
    doc.text(`Registros Exportados: ${filteredDisparos.length}`, 14, 55);
    
    const tableData = filteredDisparos.map(disparo => {
      const statusDisplay = (isAniversario && disparo.status === 'BAIXADO') ? 'Enviado' : disparo.status;
      const dataHoraEnvio = (disparo.data_envio && disparo.hora_envio && disparo.data_envio !== '' && disparo.hora_envio !== '') 
        ? `${disparo.data_envio} ${disparo.hora_envio}` : '-';
      if (isAniversario) {
        return [disparo.nome, disparo.vencimento || '-', disparo.contato, statusDisplay, dataHoraEnvio];
      }
      return [
        disparo.nome,
        disparo.placa || '-',
        disparo.modelo,
        `R$ ${(disparo.valor ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
        disparo.vencimento || '-',
        disparo.contato,
        statusDisplay,
        dataHoraEnvio
      ];
    });
    
    const headers = isAniversario 
      ? ['Nome', 'Data de Aniversário', 'Contato', 'Status', 'Data/Hora Envio']
      : ['Nome', 'Placa', 'Modelo', 'Valor', 'Vencimento', 'Contato', 'Status', 'Data/Hora Envio'];
    
    // Adicionar tabela
    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 65,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [66, 139, 202] },
      alternateRowStyles: { fillColor: [245, 245, 245] }
    });
    
    // Salvar arquivo
    doc.save(`lote_${selectedLote?.id}_${exportStatusFilter === 'all' ? 'todos' : exportStatusFilter.toLowerCase()}.pdf`);
    
    setShowExportModal(false);
    toast({
      title: "Sucesso",
      description: "Relatório PDF gerado com sucesso!",
    });
  };

  // Função para exportar em XLSX
  const exportToXLSX = () => {
    const filteredDisparos = getFilteredDisparosForExport();
    const isAniversario = selectedLote?.tipo_envio === 'ANIVERSARIO';
    
    const excelData = filteredDisparos.map(disparo => {
      const statusDisplay = (isAniversario && disparo.status === 'BAIXADO') ? 'Enviado' : disparo.status;
      const base: Record<string, string | number> = {
        'Nome': disparo.nome,
        [isAniversario ? 'Data de Aniversário' : 'Vencimento']: disparo.vencimento || '-',
        'Contato': disparo.contato,
        'Status': statusDisplay,
        'Data Envio': disparo.data_envio && disparo.hora_envio 
          ? `${disparo.data_envio} ${disparo.hora_envio}` : '-'
      };
      if (!isAniversario) {
        base['Placa'] = disparo.placa || '-';
        base['Modelo'] = disparo.modelo || '';
        base['Valor'] = disparo.valor ?? 0;
      }
      return base;
    });
    
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    const colWidths = isAniversario
      ? [{ wch: 35 }, { wch: 18 }, { wch: 15 }, { wch: 12 }, { wch: 18 }]
      : [{ wch: 30 }, { wch: 12 }, { wch: 25 }, { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 18 }];
    ws['!cols'] = colWidths;
    
    // Adicionar planilha ao workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Disparos');
    
    // Salvar arquivo
    XLSX.writeFile(wb, `lote_${selectedLote?.id}_${exportStatusFilter === 'all' ? 'todos' : exportStatusFilter.toLowerCase()}.xlsx`);
    
    setShowExportModal(false);
    toast({
      title: "Sucesso",
      description: "Planilha Excel gerada com sucesso!",
    });
  };

  // Função para executar a exportação
  const handleExport = () => {
    if (exportFormat === 'pdf') {
      exportToPDF();
    } else {
      exportToXLSX();
    }
  };

  const handleMarcarFalhasComoPendente = async (loteId: number) => {
    try {
      setIsMarcandoFalhasComoPendente(true);
      const response = await apiService.post(`/disparos/lotes/${loteId}/marcar-falhas-como-pendente`);
      toast({
        title: "Sucesso",
        description: response?.message || "Falhas colocadas em pendente. Use \"Enviar Agora\" para reenviar.",
      });
      refreshLotes();
      if (showDetailsModal && selectedLote && selectedLote.id === loteId) {
        await loadLoteDisparos(loteId);
      }
    } catch (error: any) {
      const msg = error?.response?.data?.error || "Erro ao colocar falhas em pendente";
      toast({ title: "Erro", description: msg, variant: "destructive" });
    } finally {
      setIsMarcandoFalhasComoPendente(false);
    }
  };

  const handleMarcarBaixadosComoPendente = async (loteId: number) => {
    try {
      setIsMarcandoBaixadosComoPendente(true);
      const response = await apiService.post(`/disparos/lotes/${loteId}/marcar-baixados-como-pendente`);
      toast({
        title: "Sucesso",
        description: response?.message || "Baixados colocados em pendente. Use \"Enviar Agora\" para reenviar.",
      });
      refreshLotes();
      if (showDetailsModal && selectedLote && selectedLote.id === loteId) {
        await loadLoteDisparos(loteId);
      }
    } catch (error: any) {
      const msg = error?.response?.data?.error || "Erro ao colocar baixados em pendente";
      toast({ title: "Erro", description: msg, variant: "destructive" });
    } finally {
      setIsMarcandoBaixadosComoPendente(false);
    }
  };

  const handlePauseLote = async (loteId: number) => {
    try {
      console.log(`🔄 [ClientDisparos] Tentando pausar lote ${loteId}...`);
      const response = await apiService.post(`/disparos/lotes/${loteId}/pausar`);
      console.log(`✅ [ClientDisparos] Resposta da API ao pausar:`, response);
      
      toast({
        title: "Sucesso",
        description: "Lote pausado com sucesso",
      });
      
      setTimeout(() => {
        console.log(`🔄 [ClientDisparos] Recarregando lotes após pausar...`);
        refreshLotes();
      }, 500);
      
    } catch (error) {
      console.error('❌ [ClientDisparos] Erro ao pausar lote:', error);
      toast({
        title: "Erro",
        description: "Erro ao pausar lote",
        variant: "destructive"
      });
    }
  };


  const handleDeleteLote = async (loteId: number) => {
    const confirmDelete = await confirm({
      title: 'Excluir Lote',
      description: 'Tem certeza que deseja excluir este lote?\n\nTodos os boletos relacionados serão removidos permanentemente.',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      variant: 'destructive'
    });
    if (!confirmDelete) return;

    try {
      await apiService.delete(`/disparos/lotes/${loteId}`);
      toast({
        title: 'Sucesso',
        description: 'Lote excluído com sucesso',
      });
      refreshLotes();
    } catch (error) {
      console.error('❌ [ClientDisparos] Erro ao excluir lote:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao excluir lote',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteDisparo = async (disparoId: number) => {
    const confirmDelete = await confirm({
      title: 'Excluir Disparo',
      description: 'Tem certeza que deseja excluir este disparo?\n\nEsta ação é irreversível.',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      variant: 'destructive'
    });
    if (!confirmDelete) return;

    try {
      await apiService.delete(`/disparos/${disparoId}`);
      toast({ title: 'Sucesso', description: 'Disparo excluído com sucesso' });
      if (selectedLote) {
        await loadLoteDisparos(selectedLote.id);
      }
      await refreshLotes();
    } catch (error) {
      console.error('❌ [ClientDisparos] Erro ao excluir disparo:', error);
      toast({ title: 'Erro', description: 'Erro ao excluir disparo', variant: 'destructive' });
    }
  };

  const handleEnviarAgora = async (loteId: number, enviarEmail: boolean) => {
    try {
      setIsSendingLote(true);
      const response = await apiService.post(`/disparos/lotes/${loteId}/enviar`, { enviar_email: enviarEmail });
      
      const { validCount, invalidCount } = response;
      
      toast({
        title: "Validação Concluída",
        description: `${validCount} números válidos enviados para fila. ${invalidCount} números inválidos encontrados.`,
      });
      
      refreshLotes();
      
      if (showDetailsModal && selectedLote && selectedLote.id === loteId) {
        await loadLoteDisparos(loteId);
      }
    } catch (error: any) {
      console.error('❌ [ClientDisparos] Erro ao enviar lote:', error);
      console.log('🔍 [ClientDisparos] Erro completo:', JSON.stringify(error, null, 2));
      console.log('🔍 [ClientDisparos] error.response:', error.response);
      console.log('🔍 [ClientDisparos] error.response?.data:', error.response?.data);
      
      // Verificar múltiplas estruturas possíveis de resposta de erro
      const responseData = error.response?.data || {};
      const waitSeconds = responseData.waitTimeSeconds || responseData.waitTime || responseData.wait_time_seconds;
      const nextWindowStart = responseData.nextWindowStart || responseData.next_window_start;
      const errorMessage = responseData.error || responseData.message || "";
      
      console.log('🔍 [ClientDisparos] responseData completo:', responseData);
      
      console.log('🔍 [ClientDisparos] waitSeconds:', waitSeconds);
      console.log('🔍 [ClientDisparos] nextWindowStart:', nextWindowStart);
      console.log('🔍 [ClientDisparos] errorMessage:', errorMessage);

      // 🚫 VALIDAÇÃO DE JANELA DE ENVIO DESABILITADA - Permite envio de múltiplos lotes
      // const isWindowError = waitSeconds || 
      //   errorMessage.toLowerCase().includes('janela') || 
      //   errorMessage.toLowerCase().includes('horário') ||
      //   errorMessage.toLowerCase().includes('window') ||
      //   errorMessage.toLowerCase().includes('fora') ||
      //   errorMessage.toLowerCase().includes('08:00') ||
      //   errorMessage.toLowerCase().includes('18:00');
      
      console.log('🔍 [ClientDisparos] Validação de janela de envio desabilitada');
      
      // Sempre tratar como erro genérico (sem bloqueio de múltiplos envios)
      toast({
        title: "Erro ao processar envio",
        description: errorMessage || "Não foi possível processar o envio do lote. Verifique as configurações e tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsSendingLote(false);
    }
  };

  const handleDistribuicao = (loteId: number) => {
    setSelectedLoteForDistribuicao(loteId);
    setShowDistribuicaoModal(true);
  };

  const handleConciliacao = (loteId: number) => {
    setSelectedLoteForConciliacao(loteId);
    setShowConciliacaoModal(true);
  };

  // Verificar status de distribuição para cada lote
  const checkDistribuicaoStatus = async (loteId: number) => {
    try {
      console.log(`🔍 [Distribuição] Verificando status para lote ${loteId}...`);
      const response = await apiService.get(`/disparos/lotes/${loteId}/distribuicao/status`);
      console.log(`📊 [Distribuição] Resposta para lote ${loteId}:`, response);
      
      if (response.success) {
        console.log(`✅ [Distribuição] Lote ${loteId} - hasDistribution: ${response.hasDistribution}, total: ${response.total}`);
        setDistribuicaoStatus(prev => ({
          ...prev,
          [loteId]: {
            hasDistribution: response.hasDistribution,
            total: response.total || 0
          }
        }));
      } else {
        console.warn(`⚠️ [Distribuição] Resposta sem success para lote ${loteId}:`, response);
      }
    } catch (error: any) {
      console.error(`❌ [Distribuição] Erro ao verificar status do lote ${loteId}:`, error);
      console.error(`❌ [Distribuição] Detalhes do erro:`, error.response?.data || error.message);
    }
  };

  // Verificar distribuição para todos os lotes visíveis
  useEffect(() => {
    if (lotes.length > 0) {
      console.log(`🔄 [Distribuição] Verificando status de ${lotes.length} lotes...`);
      lotes.forEach(lote => {
        checkDistribuicaoStatus(lote.id);
      });
    }
  }, [lotes]);

  const getLoteStatusBadge = (status: string) => {
    const statusConfig = {
      'PENDENTE': { label: 'Pendente', variant: 'outline' as const, icon: Clock },
      'ENVIANDO': { label: 'Enviando', variant: 'default' as const, icon: Send, color: 'bg-blue-500 hover:bg-blue-600' },
      'CONCLUÍDO': { label: 'Concluído', variant: 'default' as const, icon: CheckCircle, color: 'bg-green-500 hover:bg-green-600' },
      'PAUSADO': { label: 'Pausado', variant: 'secondary' as const, icon: Pause, color: 'bg-orange-500 hover:bg-orange-600' },
      'CANCELADO': { label: 'Cancelado', variant: 'destructive' as const, icon: XCircle },
      'EM_ANDAMENTO': { label: 'Em Andamento', variant: 'default' as const, icon: TrendingUp, color: 'bg-blue-500 hover:bg-blue-600' }
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    
    if (!config) {
      return (
        <Badge variant="outline" className="flex items-center gap-1">
          <HelpCircle className="h-3 w-3" />
          {status}
        </Badge>
      );
    }
    
    const Icon = config.icon;
    const colorClass = 'color' in config && config.color ? config.color : '';
    const customClass = 'className' in config && config.className ? config.className : '';

    return (
      <Badge variant={config.variant} className={`flex items-center gap-1 ${colorClass} ${customClass}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const formatMotivoRejeicao = (motivo?: string) => {
    if (!motivo || motivo.trim() === '') return null;
    return motivo.replace(/_/g, ' ').trim();
  };

  const getDisparoStatusBadge = (status: string, isAniversario?: boolean, motivoRejeicao?: string) => {
    const displayStatus = (isAniversario && status === 'BAIXADO') ? 'ENVIADO' : status;
    const statusConfig = {
      'ENVIADO': { label: 'Enviado', variant: 'default' as const, icon: CheckCircle, color: 'bg-green-500 hover:bg-green-600' },
      'PENDENTE': { label: 'Pendente', variant: 'outline' as const, icon: Clock },
      'FILA': { label: 'Em Fila', variant: 'secondary' as const, icon: Send },
      'AGUARDANDO_CONFIRMACAO': { label: 'Aguardando Confirmação', variant: 'secondary' as const, icon: Clock, className: 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200' },
      'FALHA': { label: 'Falha', variant: 'destructive' as const, icon: XCircle },
      'CANCELADO': { label: 'Cancelado', variant: 'secondary' as const, icon: X },
      'CONTATO_INVALIDO': { label: 'Contato Inválido', variant: 'destructive' as const, icon: AlertTriangle },
      'BAIXADO': { label: 'Baixado', variant: 'secondary' as const, icon: Download, className: 'bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200' },
      'EXCLUIDO': { label: 'Excluído', variant: 'secondary' as const, icon: Trash2 }
    };

    const config = statusConfig[displayStatus as keyof typeof statusConfig];
    
    if (!config) {
      return (
        <Badge variant="outline" className="flex items-center gap-1">
          <HelpCircle className="h-3 w-3" />
          {status}
        </Badge>
      );
    }
    
    const Icon = config.icon;
    const colorClass = 'color' in config && config.color ? config.color : '';
    const customClass = 'className' in config && config.className ? config.className : '';
    const mostraMotivo = (status === 'FALHA' || status === 'CONTATO_INVALIDO') && motivoRejeicao;

    return (
      <div className="flex flex-col gap-0.5">
        <Badge variant={config.variant} className={`flex items-center gap-1 w-fit ${colorClass} ${customClass}`}>
          <Icon className="h-3 w-3" />
          {config.label}
        </Badge>
        {mostraMotivo && (
          <span className="text-xs text-muted-foreground max-w-[200px] line-clamp-2" title={motivoRejeicao}>
            {formatMotivoRejeicao(motivoRejeicao)}
          </span>
        )}
      </div>
    );
  };

  const normalizedSearch = searchTerm.toLowerCase();
  const filteredLotes = lotes.filter(lote => {
    const nome = (lote.nome ?? '').toLowerCase();
    const tipoEnvio = (lote.tipo_envio ?? '').toLowerCase();
    return (
      nome.includes(normalizedSearch) ||
      tipoEnvio.includes(normalizedSearch)
    );
  });

  // Dados para o gráfico de status
  const chartData = [
    { name: 'Concluído', value: lotes.filter(l => l.status === 'CONCLUÍDO').length, color: '#10b981' },
    { name: 'Registrando Lote', value: lotes.filter(l => l.status === 'REGISTRANDO_LOTE').length, color: '#6b7280' },
    { name: 'Conferindo Contatos', value: lotes.filter(l => l.status === 'CONFERINDO_CONTATOS').length, color: '#f59e0b' },
    { name: 'Enviando', value: lotes.filter(l => l.status === 'ENVIANDO').length, color: '#3b82f6' },
    { name: 'Pausado', value: lotes.filter(l => l.status === 'PAUSADO').length, color: '#f97316' },
    { name: 'Pendente', value: lotes.filter(l => l.status === 'PENDENTE').length, color: '#6b7280' },
    { name: 'Em Andamento', value: lotes.filter(l => l.status === 'EM_ANDAMENTO').length, color: '#8b5cf6' },
    { name: 'Cancelado', value: lotes.filter(l => l.status === 'CANCELADO').length, color: '#ef4444' }
  ].filter(item => item.value > 0);

  const chartConfig = {
    "Concluído": { label: "Concluído", color: "#10b981" },
    "Registrando Lote": { label: "Registrando Lote", color: "#6b7280" },
    "Conferindo Contatos": { label: "Conferindo Contatos", color: "#f59e0b" },
    "Enviando": { label: "Enviando", color: "#3b82f6" },
    "Pausado": { label: "Pausado", color: "#f97316" },
    "Pendente": { label: "Pendente", color: "#6b7280" },
    "Em Andamento": { label: "Em Andamento", color: "#8b5cf6" },
    "Cancelado": { label: "Cancelado", color: "#ef4444" }
  };

  if (isLoadingLotes) {
    return (
      <Layout>
        <AtomicLoadingScreen />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-3xl font-bold tracking-tight">Disparos de Boletos</h2>
            <p className="text-muted-foreground">
              Gerencie os lotes de disparos de boletos
            </p>
          </div>
          
          {/* Central da Comunicação - sempre visível para o cliente */}
          {user?.client_id && (
            <div className="flex flex-col items-end gap-2 min-w-[200px]">
              {loadingIntegration ? (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  <span>Verificando integração...</span>
                </div>
              ) : (
                <>
                  <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                    Central da Comunicação
                  </span>
                  <ImportBoletosModal
                    clientId={user.client_id}
                    onSuccess={refreshLotes}
                    prominent
                  />
                  {hasImportIntegration && (
                    <span className="text-xs text-muted-foreground">
                      {clientSystemType === 'south' ? 'South' : clientSystemType === 'south-v0' ? 'South V0' : clientSystemType === 'hinova' ? 'Hinova SGA' : clientSystemType || 'Sistema Integrado'}
                    </span>
                  )}
                  {clientSystemType && !hasImportIntegration && (
                    <span className="text-xs text-amber-600">
                      Integração não configurada
                    </span>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total de Lotes</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{lotes.length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total de Boletos</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {lotes.reduce((acc, lote) => acc + lote.total_boletos, 0).toLocaleString('pt-BR')}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Valor Total</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                R$ {lotes.reduce((acc, lote) => acc + lote.valor_total, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Taxa de Envio</CardTitle>
              <BarChart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {lotes.length > 0 
                  ? `${Math.round(
                      (lotes.reduce((acc, l) => acc + l.total_enviados, 0) / 
                       Math.max(lotes.reduce((acc, l) => acc + l.total_boletos, 0), 1)) * 100
                    )}%`
                  : '0%'
                }
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Chart */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
          <Card className="col-span-4">
            <CardHeader>
              <CardTitle>Status dos Lotes</CardTitle>
              <CardDescription>
                Distribuição dos lotes por status
              </CardDescription>
            </CardHeader>
            <CardContent className="pl-2">
              <ChartContainer config={chartConfig}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ChartContainer>
            </CardContent>
          </Card>

          <Card className="col-span-3">
            <CardHeader>
              <CardTitle>Legenda</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {chartData.map((item) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm">{item.name}: {item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={disparoTab} onValueChange={(v) => setDisparoTab(v as 'whatsapp' | 'email')}>
          <TabsList>
            <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
            {clienteEmailCanalEnabled && <TabsTrigger value="email">Email</TabsTrigger>}
          </TabsList>

          <TabsContent value="whatsapp">
        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar lotes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Status</SelectItem>
              <SelectItem value="CONCLUÍDO">Concluído</SelectItem>
              <SelectItem value="REGISTRANDO_LOTE">Registrando Lote</SelectItem>
              <SelectItem value="CONFERINDO_CONTATOS">Conferindo Contatos</SelectItem>
              <SelectItem value="ENVIANDO">Enviando</SelectItem>
              <SelectItem value="PAUSADO">Pausado</SelectItem>
              <SelectItem value="PENDENTE">Pendente</SelectItem>
              <SelectItem value="EM_ANDAMENTO">Em Andamento</SelectItem>
              <SelectItem value="CANCELADO">Cancelado</SelectItem>
            </SelectContent>
          </Select>

          {/* Seletor de Conexão Simples */}
          {loadingConnections ? (
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
              <span>Carregando conexões...</span>
            </div>
          ) : clientConnections.length > 1 ? (
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">Conexão:</span>
              <Select 
                value={selectedConnectionId?.toString() || ''} 
                onValueChange={(value) => setSelectedConnectionId(parseInt(value))}
              >
                <SelectTrigger className="w-[250px]">
                  <SelectValue placeholder="Selecione a conexão" />
                </SelectTrigger>
                <SelectContent>
                  {clientConnections.map((connection) => (
                    <SelectItem key={connection.id} value={connection.id.toString()}>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{connection.name}</span>
                        <Badge 
                          variant="outline" 
                          className={connection.is_online ? "text-green-600 border-green-600" : "text-gray-500 border-gray-400"}
                        >
                          {connection.is_online ? 'Online' : 'Offline'}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : clientConnections.length === 1 ? (
            <div className="text-sm text-gray-600">
              <span>Conexão: <strong>{clientConnections[0].name}</strong></span>
            </div>
          ) : (
            <div className="text-sm text-gray-500">
              <span>Nenhuma conexão encontrada</span>
            </div>
          )}
        </div>

        {/* Progresso Diário - Limite de Disparos */}
        {user?.client_id && (
          <DailyProgressVisualization 
            clientId={user.client_id} 
            selectedConnectionId={selectedConnectionId}
          />
        )}

        {/* Lotes Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Lotes de Disparos</CardTitle>
                <CardDescription>
                  Visualize e gerencie seus lotes de disparos de boletos (últimos 7 dias)
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowHistoricoModal(true)}
                  className="flex items-center gap-2"
                >
                  <Calendar className="h-4 w-4" />
                  Histórico de Lotes
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome do Lote</TableHead>
                  <TableHead>Tipo de Envio</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Total Boletos</TableHead>
                  <TableHead className="text-center">Enviados</TableHead>
                  <TableHead className="text-center">Aguard. Conf.</TableHead>
                  <TableHead className="text-center">Em Fila</TableHead>
                  <TableHead className="text-center">Pendentes</TableHead>
                  <TableHead className="text-center">Inválidos</TableHead>
                  <TableHead className="text-center">Bloqueados</TableHead>
                  <TableHead className="text-center">Falhas</TableHead>
                  <TableHead className="text-center">Baixados</TableHead>
                  <TableHead className="text-right">Valor Total</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLotes.map((lote) => (
                  <TableRow key={lote.id}>
                    <TableCell className="font-medium">{lote.nome || `Lote #${lote.id}`}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {lote.tipo_envio}
                      </Badge>
                    </TableCell>
                    <TableCell>{getLoteStatusBadge(lote.status)}</TableCell>
                    <TableCell className="text-center">{lote.total_boletos}</TableCell>
                    <TableCell className="text-center">{lote.total_enviados}</TableCell>
                    <TableCell className="text-center">{lote.total_aguardando_confirmacao ?? 0}</TableCell>
                    <TableCell className="text-center">{lote.total_fila || 0}</TableCell>
                    <TableCell className="text-center">{lote.total_pendentes}</TableCell>
                    <TableCell className="text-center">{lote.total_invalidos || 0}</TableCell>
                    <TableCell className="text-center">{lote.total_bloqueados || 0}</TableCell>
                    <TableCell className="text-center">{lote.total_falhas}</TableCell>
                    <TableCell className="text-center">{lote.total_baixados || 0}</TableCell>
                    <TableCell className="text-right">
                      R$ {(lote.valor_total ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>
                      {formatDateBR(lote.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm" onClick={() => handleViewDetails(lote)}>
                          <Eye className="mr-2 h-4 w-4" />
                          Ver Detalhes
                        </Button>
                        
                        {/* Botão Distribuição */}
                        {distribuicaoStatus[lote.id]?.hasDistribution ? (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDistribuicao(lote.id)}
                            className="border-green-500 text-green-700 hover:bg-green-50"
                          >
                            <UserCheck className="mr-2 h-4 w-4" />
                            Já distribuído ({distribuicaoStatus[lote.id].total})
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDistribuicao(lote.id)}
                            className="border-blue-500 text-blue-600 hover:bg-blue-50"
                          >
                            <UserCheck className="mr-2 h-4 w-4" />
                            Distribuição
                          </Button>
                        )}

                        {/* Botao Conciliar */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleConciliacao(lote.id)}
                          className="border-orange-500 text-orange-700 hover:bg-orange-50"
                        >
                          <FileSearch className="mr-2 h-4 w-4" />
                          Conciliar
                        </Button>

                        {lote.status === 'ENVIANDO' && (
                          <Button 
                            variant="secondary" 
                            size="sm" 
                            onClick={() => handlePauseLote(lote.id)}
                          >
                            <Pause className="mr-2 h-4 w-4" />
                            Pausar
                          </Button>
                        )}
                        {(lote.total_falhas ?? 0) > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarcarFalhasComoPendente(lote.id)}
                            disabled={isMarcandoFalhasComoPendente}
                            className="border-amber-500 text-amber-700 hover:bg-amber-50"
                          >
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Reenviar falhas
                          </Button>
                        )}
                        {(lote.total_baixados ?? 0) > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarcarBaixadosComoPendente(lote.id)}
                            disabled={isMarcandoBaixadosComoPendente}
                            className="border-purple-500 text-purple-700 hover:bg-purple-50"
                          >
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Reenviar baixados
                          </Button>
                        )}
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          onClick={() => handleDeleteLote(lote.id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Excluir
                        </Button>
                        
                        {/* Botão Enviar Agora - não mostrar para lotes concluídos */}
                        {lote.status !== 'CONCLUÍDO' && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => { setEnviarEmailJunto(clienteEmailCanalEnabled); setEnviarDialog({ open: true, loteId: lote.id, emailEnabled: clienteEmailCanalEnabled }); }}
                            disabled={isSendingLote}
                            className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                          >
                            <Send className="mr-2 h-4 w-4" />
                            {isSendingLote ? 'Enviando...' : 'Enviar Agora'}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
          </TabsContent>

          {clienteEmailCanalEnabled && (
            <TabsContent value="email">
              <EmailLotesPanel clientId={user?.client_id ?? null} />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Lote Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes do Lote</DialogTitle>
            <DialogDescription>
              {selectedLote?.nome} - Visualize todos os disparos do lote
            </DialogDescription>
          </DialogHeader>

          {/* Botões Exportar, Colocar falhas em pendente e Colocar baixados em pendente */}
          <div className="flex justify-end gap-2 mb-4">
            {selectedLote && (selectedLote.total_falhas ?? 0) > 0 && (
              <Button
                variant="outline"
                onClick={() => handleMarcarFalhasComoPendente(selectedLote.id)}
                disabled={isMarcandoFalhasComoPendente}
                className="flex items-center gap-2 border-amber-500 text-amber-700 hover:bg-amber-50"
              >
                {isMarcandoFalhasComoPendente ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {isMarcandoFalhasComoPendente ? 'Colocando...' : 'Colocar falhas em pendente'}
              </Button>
            )}
            {selectedLote && (selectedLote.total_baixados ?? 0) > 0 && (
              <Button
                variant="outline"
                onClick={() => handleMarcarBaixadosComoPendente(selectedLote.id)}
                disabled={isMarcandoBaixadosComoPendente}
                className="flex items-center gap-2 border-purple-500 text-purple-700 hover:bg-purple-50"
              >
                {isMarcandoBaixadosComoPendente ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {isMarcandoBaixadosComoPendente ? 'Colocando...' : 'Colocar baixados em pendente'}
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => setShowExportModal(true)}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Exportar Lote
            </Button>
          </div>

          {selectedLote && (
            <div className="space-y-6">
              {(() => {
                const isAniversario = selectedLote.tipo_envio === 'ANIVERSARIO';
                return (
              <>
              {/* Lote Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-semibold text-sm text-gray-600">Informações do Lote</h3>
                  <div className="mt-2 space-y-1">
                    <p><span className="font-medium">Nome:</span> {selectedLote.nome}</p>
                    <p><span className="font-medium">Tipo de Envio:</span> {selectedLote.tipo_envio}</p>
                    <div className="flex items-center gap-2"><span className="font-medium">Status:</span> {getLoteStatusBadge(selectedLote.status)}</div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-gray-600">Estatísticas</h3>
                  <div className="mt-2 space-y-1">
                    <p><span className="font-medium">{isAniversario ? 'Total de Aniversariantes' : 'Total de Boletos'}:</span> {selectedLote.total_boletos}</p>
                    <p><span className="font-medium">Enviados:</span> {selectedLote.total_enviados}</p>
                    <p><span className="font-medium">Aguardando Confirmação:</span> {selectedLote.total_aguardando_confirmacao ?? 0}</p>
                    <p><span className="font-medium">Pendentes:</span> {selectedLote.total_pendentes}</p>
                    <p><span className="font-medium">Inválidos:</span> {selectedLote.total_invalidos || 0}</p>
                    <p><span className="font-medium">Falhas:</span> {selectedLote.total_falhas}</p>
                    {!isAniversario && (
                      <p><span className="font-medium">Valor Total:</span> R$ {(selectedLote.valor_total ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Search and Filter Disparos */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Buscar disparos..."
                    value={disparoSearchTerm}
                    onChange={(e) => setDisparoSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="status-filter" className="text-sm font-medium whitespace-nowrap">
                    Status:
                  </Label>
                  <Select value={disparoStatusFilter} onValueChange={setDisparoStatusFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filtrar por status" />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Badge variant="outline" className="px-3 py-1">
                  {loteDisparos.filter(disparo => {
                    const matchesSearch = disparo.nome.toLowerCase().includes(disparoSearchTerm.toLowerCase()) ||
                      (disparo.placa && disparo.placa.toLowerCase().includes(disparoSearchTerm.toLowerCase())) ||
                      disparo.contato.includes(disparoSearchTerm) ||
                      (disparo.vencimento && disparo.vencimento.includes(disparoSearchTerm));
                    
                    const matchesStatus = disparoStatusFilter === 'all' || disparo.status === disparoStatusFilter;
                    
                    return matchesSearch && matchesStatus;
                  }).length} de {loteDisparos.length} disparos
                </Badge>
              </div>

              {/* Disparos Table - Colunas condicionais por tipo */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      {!isAniversario && <TableHead>Placa</TableHead>}
                      {!isAniversario && <TableHead>Modelo</TableHead>}
                      {!isAniversario && <TableHead>Valor</TableHead>}
                      <TableHead>{isAniversario ? 'Data de Aniversário' : 'Vencimento'}</TableHead>
                      <TableHead>Contato</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Data/Hora Envio</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {disparosLoading ? (
                      <TableRow>
                        <TableCell colSpan={isAniversario ? 6 : 9} className="text-center py-8">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                            <span className="ml-2">Carregando disparos...</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      loteDisparos
                        .filter(disparo => {
                          const matchesSearch = disparo.nome.toLowerCase().includes(disparoSearchTerm.toLowerCase()) ||
                            (disparo.placa && disparo.placa.toLowerCase().includes(disparoSearchTerm.toLowerCase())) ||
                            disparo.contato.includes(disparoSearchTerm) ||
                            (disparo.vencimento && disparo.vencimento.includes(disparoSearchTerm));
                          
                          const matchesStatus = disparoStatusFilter === 'all' || disparo.status === disparoStatusFilter;
                          
                          return matchesSearch && matchesStatus;
                        })
                        .map((disparo) => (
                          <TableRow key={disparo.id}>
                            <TableCell className="font-medium">{disparo.nome}</TableCell>
                            {!isAniversario && (
                              <TableCell>{disparo.placa && disparo.placa.trim() ? disparo.placa : '-'}</TableCell>
                            )}
                            {!isAniversario && <TableCell>{disparo.modelo}</TableCell>}
                            {!isAniversario && (
                              <TableCell>
                                R$ {(disparo.valor ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </TableCell>
                            )}
                            <TableCell>
                              {disparo.vencimento || '-'}
                            </TableCell>
                            <TableCell>{disparo.contato}</TableCell>
                            <TableCell>
                              {getDisparoStatusBadge(disparo.status, isAniversario, disparo.motivo_rejeicao)}
                            </TableCell>
                            <TableCell>
                              {disparo.data_envio && disparo.hora_envio 
                                ? `${disparo.data_envio} ${disparo.hora_envio}`
                                : '-'
                              }
                            </TableCell>
                            <TableCell>
                              <Button 
                                variant="destructive" 
                                size="icon" 
                                onClick={() => handleDeleteDisparo(disparo.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </div>
              </>
            );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Exportar Lote</DialogTitle>
            <DialogDescription>
              Selecione o formato e filtros para exportação do lote {selectedLote?.id}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Seleção de Formato */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Formato de Exportação</Label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant={exportFormat === 'pdf' ? 'default' : 'outline'}
                  onClick={() => setExportFormat('pdf')}
                  className="flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" />
                  PDF
                </Button>
                <Button
                  variant={exportFormat === 'xlsx' ? 'default' : 'outline'}
                  onClick={() => setExportFormat('xlsx')}
                  className="flex items-center gap-2"
                >
                  <Database className="h-4 w-4" />
                  Excel
                </Button>
              </div>
            </div>

            {/* Filtro por Status */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Filtrar por Status</Label>
              <Select value={exportStatusFilter} onValueChange={setExportStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Informações da Exportação */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-sm text-gray-600">
                <p><strong>Total de disparos:</strong> {loteDisparos.length}</p>
                <p><strong>Serão exportados:</strong> {getFilteredDisparosForExport().length}</p>
                <p><strong>Formato:</strong> {exportFormat.toUpperCase()}</p>
              </div>
            </div>

            {/* Botões de Ação */}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowExportModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleExport} className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Exportar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Distribuição */}
      <ImportDistribuicaoModal
        isOpen={showDistribuicaoModal}
        onClose={() => {
          setShowDistribuicaoModal(false);
          setSelectedLoteForDistribuicao(null);
        }}
        loteId={selectedLoteForDistribuicao || 0}
        onSuccess={() => {
          refreshLotes();
          if (selectedLoteForDistribuicao) {
            checkDistribuicaoStatus(selectedLoteForDistribuicao);
          }
          setShowDistribuicaoModal(false);
          setSelectedLoteForDistribuicao(null);
        }}
      />
      
      {/* Modal de Conciliacao */}
      <ConciliarBoletosModal
        isOpen={showConciliacaoModal}
        onClose={() => {
          setShowConciliacaoModal(false);
          setSelectedLoteForConciliacao(null);
        }}
        loteId={selectedLoteForConciliacao || 0}
        onSuccess={() => {
          refreshLotes();
          setShowConciliacaoModal(false);
          setSelectedLoteForConciliacao(null);
        }}
      />

      {/* Modal de Histórico de Lotes */}
      <HistoricoLotesModal
        isOpen={showHistoricoModal}
        onClose={() => setShowHistoricoModal(false)}
        onViewDetails={(lote) => {
          setSelectedLote(lote);
          setShowDetailsModal(true);
          setShowHistoricoModal(false);
        }}
        user={user}
        clienteId={user?.client_id}
      />

      {/* Dialog de Confirmação */}
      <ConfirmationDialog />

      <AlertDialog open={enviarDialog.open} onOpenChange={(open) => setEnviarDialog((s) => ({ ...s, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enviar lote agora?</AlertDialogTitle>
            <AlertDialogDescription>
              Os boletos serão enfileirados para envio no WhatsApp.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {enviarDialog.emailEnabled && (
            <div className="flex items-center gap-2 py-2">
              <Checkbox
                id="enviar-email-junto"
                checked={enviarEmailJunto}
                onCheckedChange={(v) => setEnviarEmailJunto(v === true)}
              />
              <label htmlFor="enviar-email-junto" className="text-sm">
                Enviar também por email
              </label>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const id = enviarDialog.loteId;
                setEnviarDialog({ open: false, loteId: null, emailEnabled: false });
                if (id != null) handleEnviarAgora(id, enviarDialog.emailEnabled ? enviarEmailJunto : false);
              }}
            >
              Enviar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </Layout>
  );
};

const ClientDisparos = () => {
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState<string>('all');

  if (!user) {
    return (
      <Layout>
        <AtomicLoadingScreen />
      </Layout>
    );
  }

  return (
    <DisparosProvider user={user} clienteId={user.client_id} statusFilter={statusFilter}>
      <ClientDisparosContent />
    </DisparosProvider>
  );
};

export default ClientDisparos;
